/* eslint-disable no-unused-vars */
import { TNotation } from "./TYPENotations";

// LQC-2493 - futures contract reference data from GET /Securities/Futures (built by
// LQC-2496). Same job as ENUMBondSecurity: the searchable universe a user picks a
// contract from, so the contract sent must already exist in our database.
//
// Field names are taken from SecurityFutureModel
// (LQPortal/LQ.Application.Models/Securities/SecuritiesFutureModel.cs), camelCased by
// the API default naming policy - the same route that turns SecurityBondModel.ISIN into
// the "isin" this app already reads.
//
// The contract is identified to the back end by its Bloomberg symbol ("ERH7"), sent with
// identifierType "Bloomberg Symbol".

export enum FutureSecurityField {
  bloombergSymbol = "bloombergSymbol",
  securityName = "securityName",
  exchange = "exchange",
  currency = "currency",
  contractAmount = "contractAmount",
  maturityDate = "maturityDate",
  expiryDate = "expiryDate",
  settlementDays = "settlementDays",
  risk = "risk",
  bidPrice = "bidPrice",
  bidPriceDate = "bidPriceDate",
  askPrice = "askPrice",
  askPriceDate = "askPriceDate",
  midPrice = "midPrice",
  midPriceDate = "midPriceDate",
  notation = "notation",
}

// Only bloombergSymbol and securityName are non-nullable server side; everything else
// comes back null when the row has no value. maturityDate is date-only (2027-03-15),
// the price dates are full timestamps.
//
// SecurityFutureModel also declares settlementDate, but GetFuturesWithPrices does not
// map it, so it is never populated. Deliberately not modelled; settlementDays is used.
//
// The prices are served but not yet shown: the ticket displays the fields LQC-2494
// asked for. They are modelled because they are part of the contract and are what an
// indicative price row would use, exactly as the bond ticket does.
export interface IFutureSecurity {
  [FutureSecurityField.bloombergSymbol]: string;
  [FutureSecurityField.securityName]: string;
  [FutureSecurityField.exchange]: string | null;
  [FutureSecurityField.currency]: string | null;
  [FutureSecurityField.contractAmount]: number | null;
  [FutureSecurityField.maturityDate]: string | null;
  // LQC-2979. The date a future is known by (spec F11); expired contracts are filtered out server side.
  [FutureSecurityField.expiryDate]?: string | null;
  // LQC-2979. Settlement is T+settlementDays (LQC-2995); expected 0.
  [FutureSecurityField.settlementDays]?: number | null;
  // LQC-2945. dbo.KeyRatio "Risk"; null for every contract locally (LQC-2941).
  [FutureSecurityField.risk]?: number | null;
  [FutureSecurityField.bidPrice]: number | null;
  [FutureSecurityField.bidPriceDate]: string | null;
  [FutureSecurityField.askPrice]: number | null;
  [FutureSecurityField.askPriceDate]: string | null;
  [FutureSecurityField.midPrice]: number | null;
  [FutureSecurityField.midPriceDate]: string | null;
  [FutureSecurityField.notation]: TNotation | null;
}

// GET /Securities/Futures
export interface IFutureSecuritiesResponse {
  futures: IFutureSecurity[];
}

// What a user types to find a contract. Symbol first - it is what gets sent - then the
// readable name, then the exchange for a broader sweep.
export const FUTURE_SECURITY_SEARCH_FIELDS: FutureSecurityField[] = [
  FutureSecurityField.bloombergSymbol,
  FutureSecurityField.securityName,
  FutureSecurityField.exchange,
];

// The field that identifies a contract to the back end.
export const FUTURE_SECURITY_KEY_FIELD = FutureSecurityField.bloombergSymbol;
